import { useQuery } from "@tanstack/react-query";
import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type FormEvent,
} from "react";

import { AuthenticationContext } from "../../app/auth/authState";
import { canReviewCurveLibrary } from "../../app/auth/curveLibraryAccess";
import { curveLibraryApi, type CurveLibraryApi } from "./libraryApi";
import { CurveLibrarySubmissionPanel } from "./CurveLibrarySubmissionPanel";
import type { CatalogApi } from "../catalog/api";
import type {
  PumpRevisionInput,
  PumpTemplateCreateInput,
  PumpTemplateRead,
} from "../catalog/types";
import type { CurveImportApi } from "./api";
import { PublicationEditor } from "./PublicationEditor";
import {
  curveFamilyLabel,
  curveGroupLabel,
  curveGroupReadiness,
  isContourIntersectionGap,
  pruneIncompleteCurveGroups,
} from "./curveReadiness";
import { readableCurveError } from "./errors";
import {
  createPublicationDraft,
  publicationEngineeringChanged,
  publicationMatchesDocument,
  publicationPayload,
  publicationReady,
} from "./publicationState";

import type { PublicationDraft } from "./publicationState";
import type {
  CurveDraftDocument,
  CurveImportRead,
  CurveSetRevisionRead,
  CurveTraceDraft,
  VerifyImportInput,
} from "./types";
import { guided } from "../../app/guidance/fieldHelpRegistryCore";
import { curveImportFieldHelp } from "../../app/guidance/fieldHelpRegistry";

interface ReviewPublicationPanelProps {
  api: CurveImportApi;
  library?: CurveLibraryApi;
  catalog: CatalogApi;
  disabled?: boolean;
  document: CurveDraftDocument;
  hasUnsavedChanges: boolean;
  imported: CurveImportRead;
  onBusyChange?: (busy: boolean) => void;
  onError: (message: string) => void;
  onDirtyChange?: (dirty: boolean) => void;
  onImported: (imported: CurveImportRead) => void;
  onNotice: (message: string) => void;
  onCorrect?: () => void;
}

function rangeText(values: number[]): string {
  const minimum = Math.min(...values);
  const maximum = Math.max(...values);
  const format = (value: number) => Number(value.toPrecision(6)).toString();
  return minimum === maximum
    ? format(minimum)
    : `${format(minimum)}–${format(maximum)}`;
}
function normalizeCurveIdentity(value: string): string {
  return value.trim().replaceAll(/\s+/gu, " ").toLowerCase();
}

interface CatalogVariantOption {
  id: string;
  impellerDiameterM: number | null;
  name: string;
  trimDescription: string | null;
}

function catalogVariants(value: unknown): CatalogVariantOption[] {
  if (!Array.isArray(value)) return [];
  const candidates: unknown[] = value;
  return candidates.flatMap((candidate) => {
    if (
      typeof candidate === "object" &&
      candidate !== null &&
      "id" in candidate &&
      "name" in candidate &&
      typeof candidate.id === "string" &&
      typeof candidate.name === "string"
    ) {
      const impellerDiameterM =
        "impeller_diameter_m" in candidate &&
        typeof candidate.impeller_diameter_m === "number" &&
        Number.isFinite(candidate.impeller_diameter_m)
          ? candidate.impeller_diameter_m
          : null;
      const trimDescription =
        "trim_description" in candidate &&
        typeof candidate.trim_description === "string"
          ? candidate.trim_description
          : null;
      return [
        {
          id: candidate.id,
          impellerDiameterM,
          name: candidate.name,
          trimDescription,
        },
      ];
    }
    return [];
  });
}

function uniqueWarningCodes(imported: CurveImportRead): string[] {
  return [
    ...new Set(
      imported.findings
        .filter((finding) => finding.severity === "warning")
        .map((finding) => finding.code),
    ),
  ];
}

const SOURCE_CONTOUR_KINDS = new Set<CurveTraceDraft["kind"]>([
  "brake_power_contour",
  "efficiency_contour",
  "npshr_contour",
]);

function isSourceContour(trace: CurveTraceDraft): boolean {
  return SOURCE_CONTOUR_KINDS.has(trace.kind);
}

function traceCanBeShared(trace: CurveTraceDraft): boolean {
  return (
    isSourceContour(trace) ||
    (trace.kind !== "head" && trace.trim_label === null)
  );
}

function sameTrim(left: CurveTraceDraft, right: CurveTraceDraft): boolean {
  return (left.trim_label ?? null) === (right.trim_label ?? null);
}

function sourceContourApplies(
  contour: CurveTraceDraft,
  head: CurveTraceDraft,
  testedSpeed: number | null,
): boolean {
  const speedMatches =
    contour.speed_rpm === null || contour.speed_rpm === testedSpeed;
  const trimMatches = contour.trim_label === null || sameTrim(contour, head);
  return speedMatches && trimMatches;
}

interface ExtractedPumpSeed {
  dischargeDiameterM: number | null;
  dischargeText: string | null;
  drawingNumber: string | null;
  impellerDiameterM: number | null;
  impellerText: string | null;
  manufacturer: string | null;
  missing: string[];
  model: string | null;
  pumpSizeText: string | null;
  revisionLabel: string;
  suctionDiameterM: number | null;
  suctionText: string | null;
}

function cleanText(value: string | null | undefined): string | null {
  const cleaned = value?.trim() ?? "";
  return cleaned === "" ? null : cleaned;
}

function lengthTextToMetres(value: string | null): number | null {
  if (value === null) return null;
  const match = /^([0-9]+(?:\.[0-9]+)?)\s*(mm|cm|m|in|inch|inches|["″])$/iu.exec(
    value.trim(),
  );
  if (match === null) return null;
  const magnitude = Number(match[1]);
  const unit = match[2]?.toLowerCase();
  const factor =
    unit === "mm"
      ? 0.001
      : unit === "cm"
        ? 0.01
        : unit === "m"
          ? 1
          : 0.0254;
  return Number.isFinite(magnitude) && magnitude > 0
    ? magnitude * factor
    : null;
}

function trimLabelToMetres(value: string | null): number | null {
  if (value === null) return null;
  const match = /([0-9]+(?:\.[0-9]+)?)\s*(mm|cm|m|in|inch|inches|["″])/iu.exec(
    value.trim(),
  );
  if (match === null) return null;
  return lengthTextToMetres(`${match[1]} ${match[2]}`);
}

function trimIdentity(value: string): string {
  return normalizeCurveIdentity(value).replaceAll(/[.]/gu, "");
}

interface TrimVariantSeed {
  diameterM: number;
  label: string;
}

function hasMultipleTrimsAtOneSpeed(document: CurveDraftDocument): boolean {
  const trimsBySpeed = new Map<string, Set<string>>();
  for (const head of document.traces.filter((trace) => trace.kind === "head")) {
    const speedKey = head.speed_rpm === null ? "unknown" : String(head.speed_rpm);
    const label = cleanText(head.trim_label);
    const trimKey = label === null ? `unlabeled:${head.id}` : trimIdentity(label);
    const trims = trimsBySpeed.get(speedKey) ?? new Set<string>();
    trims.add(trimKey);
    trimsBySpeed.set(speedKey, trims);
  }
  return [...trimsBySpeed.values()].some((trims) => trims.size > 1);
}

function multiTrimVariantSeeds(
  document: CurveDraftDocument,
): { invalid: boolean; seeds: TrimVariantSeed[] } | null {
  if (!hasMultipleTrimsAtOneSpeed(document)) return null;
  const seeds: TrimVariantSeed[] = [];
  const seen = new Set<string>();
  let invalid = false;
  for (const head of document.traces.filter((trace) => trace.kind === "head")) {
    const label = cleanText(head.trim_label);
    const diameterM = trimLabelToMetres(label);
    if (label === null || diameterM === null) {
      invalid = true;
      continue;
    }
    const key = trimIdentity(label);
    if (seen.has(key)) continue;
    seen.add(key);
    seeds.push({ diameterM, label });
  }
  return { invalid, seeds };
}

function publicationHead(
  publication: PublicationDraft,
  document: CurveDraftDocument,
): CurveTraceDraft | null {
  return (
    document.traces.find(
      (trace) =>
        trace.kind === "head" &&
        publication.measuredTraceIds.includes(trace.id),
    ) ?? null
  );
}

function publicationGroupLabel(
  publication: PublicationDraft,
  document: CurveDraftDocument,
  index: number,
  includeTrim: boolean,
): string {
  const head = publicationHead(publication, document);
  const speed =
    publication.testedSpeed === ""
      ? "Speed TBD"
      : `${publication.testedSpeed} RPM`;
  const trim = cleanText(head?.trim_label);
  if (includeTrim && trim !== null) return `${trim} · ${speed}`;
  return head === null && publication.testedSpeed === ""
    ? `Curve ${index + 1}`
    : speed;
}

interface PublicationVariantGroup {
  key: string;
  label: string;
  publicationIds: string[];
  variantId: string;
}

function publicationVariantGroups(
  publications: PublicationDraft[],
  document: CurveDraftDocument,
): PublicationVariantGroup[] {
  const groups = new Map<string, PublicationVariantGroup>();
  for (const [index, publication] of publications.entries()) {
    const head = publicationHead(publication, document);
    const trim = cleanText(head?.trim_label);
    const key = trim === null ? publication.id : trimIdentity(trim);
    const existing = groups.get(key);
    if (existing === undefined) {
      groups.set(key, {
        key,
        label:
          trim ??
          publicationGroupLabel(publication, document, index, false),
        publicationIds: [publication.id],
        variantId: publication.catalogVariantId,
      });
      continue;
    }
    existing.publicationIds.push(publication.id);
    if (existing.variantId !== publication.catalogVariantId) {
      existing.variantId = "";
    }
  }
  return [...groups.values()];
}

function variantForTrim(
  variants: CatalogVariantOption[],
  label: string,
  diameterM: number,
): CatalogVariantOption | null {
  const identity = trimIdentity(label);
  const exact = variants.filter(
    (variant) =>
      trimIdentity(variant.name) === identity ||
      (variant.trimDescription !== null &&
        trimIdentity(variant.trimDescription) === identity),
  );
  if (exact.length === 1) return exact[0] ?? null;
  const diameterMatches = variants.filter(
    (variant) =>
      variant.impellerDiameterM !== null &&
      Math.abs(variant.impellerDiameterM - diameterM) <= 0.000_01,
  );
  return diameterMatches.length === 1
    ? (diameterMatches[0] ?? null)
    : null;
}

function extractedPumpSeed(imported: CurveImportRead): ExtractedPumpSeed {
  const aiMetadata =
    imported.suggestions.ai_suggestion_runs.at(-1)?.suggestion.metadata;
  const manufacturer = cleanText(
    imported.suggestions.manufacturer ?? aiMetadata?.manufacturer,
  );
  const model = cleanText(imported.suggestions.model ?? aiMetadata?.model);
  const suctionText = cleanText(
    imported.suggestions.suction_size_text ?? aiMetadata?.suction_size_text,
  );
  const dischargeText = cleanText(
    imported.suggestions.discharge_size_text ??
      aiMetadata?.discharge_size_text,
  );
  const impellerText = cleanText(
    imported.suggestions.impeller_diameter_text ??
      aiMetadata?.impeller_diameter_text,
  );
  const suctionDiameterM = lengthTextToMetres(suctionText);
  const dischargeDiameterM = lengthTextToMetres(dischargeText);
  const impellerDiameterM = lengthTextToMetres(impellerText);
  const missing = [
    ...(manufacturer === null ? ["manufacturer"] : []),
    ...(model === null ? ["model"] : []),
    ...(suctionDiameterM === null ? ["suction size with units"] : []),
    ...(dischargeDiameterM === null ? ["discharge size with units"] : []),
    ...(impellerText !== null && impellerDiameterM === null
      ? ["impeller size with units"]
      : []),
  ];
  return {
    dischargeDiameterM,
    dischargeText,
    drawingNumber: cleanText(
      imported.suggestions.drawing_number ?? aiMetadata?.drawing_number,
    ),
    impellerDiameterM,
    impellerText,
    manufacturer,
    missing,
    model,
    pumpSizeText: cleanText(
      imported.suggestions.pump_size_text ?? aiMetadata?.pump_size_text,
    ),
    revisionLabel:
      cleanText(
        imported.suggestions.curve_revision ?? aiMetadata?.curve_revision,
      ) ?? "A",
    suctionDiameterM,
    suctionText,
  };
}

function initialPublications(
  document: CurveDraftDocument,
  imported: CurveImportRead,
): PublicationDraft[] {
  const baseName =
    imported.suggestions.model ??
    imported.suggestions.curve_number ??
    "Pump curve";
  const revisionLabel = imported.suggestions.curve_revision ?? "A";
  const fallbackSpeed =
    imported.suggestions.speeds_rpm.length === 1
      ? (imported.suggestions.speeds_rpm[0] ?? null)
      : null;
  const heads = document.traces.filter((trace) => trace.kind === "head");
  if (heads.length === 0) {
    return [createPublicationDraft(baseName, revisionLabel, fallbackSpeed, [])];
  }
  return heads.map((head) => {
    const speed = head.speed_rpm ?? fallbackSpeed;
    const associatedFunctions = document.traces.filter(
      (trace) =>
        trace.id !== head.id &&
        trace.kind !== "head" &&
        !isSourceContour(trace) &&
        trace.speed_rpm === speed &&
        (trace.trim_label === null || sameTrim(trace, head)),
    );
    const sourceContours = document.traces.filter(
      (trace) =>
        isSourceContour(trace) &&
        sourceContourApplies(trace, head, speed),
    );
    const traceIds = [
      head.id,
      ...associatedFunctions.map((trace) => trace.id),
      ...sourceContours.map((trace) => trace.id),
    ];
    const suffix =
      heads.length === 1
        ? ""
        : ` — ${speed === null ? "speed TBD" : `${speed} RPM`}${head.trim_label === null ? "" : `, ${head.trim_label}`}`;
    const publication = createPublicationDraft(
      `${baseName}${suffix}`,
      revisionLabel,
      speed,
      traceIds,
    );
    const impellerDiameterM = trimLabelToMetres(head.trim_label);
    return {
      ...publication,
      impellerDiameter:
        impellerDiameterM === null ? "" : String(impellerDiameterM),
    };
  });
}

function pumpCreatePayload(
  imported: CurveImportRead,
  seed: ExtractedPumpSeed,
  pumpType: PumpRevisionInput["pump_type"] | "",
  trimVariants: TrimVariantSeed[] | null,
): PumpTemplateCreateInput | null {
  const missing =
    trimVariants === null
      ? seed.missing
      : seed.missing.filter((item) => item !== "impeller size with units");
  if (
    missing.length > 0 ||
    pumpType === "" ||
    seed.manufacturer === null ||
    seed.model === null ||
    seed.suctionDiameterM === null ||
    seed.dischargeDiameterM === null ||
    (trimVariants !== null && trimVariants.length < 2)
  ) {
    return null;
  }
  const trimNotes =
    trimVariants === null
      ? seed.impellerText
      : trimVariants.map((variant) => variant.label).join(", ").slice(0, 1000);
  return {
    template_name: `${seed.manufacturer} ${seed.model}`.slice(0, 200),
    revision: {
      confidence: "low",
      discharge_diameter_m: seed.dischargeDiameterM,
      expected_maximum_gear_ratio: null,
      expected_minimum_gear_ratio: null,
      impeller_trim_notes: trimNotes,
      manufacturer: seed.manufacturer,
      maximum_suction_lift_m: null,
      minimum_suction_pressure_absolute_pa: null,
      model: seed.model,
      notes:
        seed.drawingNumber === null
          ? null
          : `Drawing ${seed.drawingNumber}`,
      provenance: {
        captured_at: new Date().toISOString(),
        source_kind: "vendor_document",
        source_name: `Curve sheet: ${imported.source.original_filename}`.slice(
          0,
          200,
        ),
      },
      pump_type: pumpType,
      revision_label: seed.revisionLabel,
      suction_diameter_m: seed.suctionDiameterM,
      variants:
        trimVariants === null
          ? [{
              curve_links: [],
              impeller_diameter_m: seed.impellerDiameterM,
              name: (seed.impellerText ?? "Standard").slice(0, 200),
              trim_description: seed.impellerText,
            }]
          : trimVariants.map((variant) => ({
              curve_links: [],
              impeller_diameter_m: variant.diameterM,
              name: variant.label.slice(0, 200),
              trim_description: variant.label,
            })),
      vendor_maximum_power_w: null,
      vendor_maximum_pressure_gauge_pa: null,
      vendor_maximum_pump_speed_rpm: null,
      vendor_minimum_pump_speed_rpm: null,
      verification_state: "draft",
    },
  };
}

function initialLinkedPublications(
  document: CurveDraftDocument,
  imported: CurveImportRead,
): PublicationDraft[] {
  return initialPublications(document, imported).map((publication) => ({
    ...publication,
    linkCatalog: true,
  }));
}

export function ReviewPublicationPanel({
  library = curveLibraryApi,
  api,
  catalog,
  disabled = false,
  document,
  onBusyChange,
  hasUnsavedChanges,
  imported,
  onDirtyChange,
  onError,
  onImported,
  onNotice,
  onCorrect,
}: ReviewPublicationPanelProps) {
  const authentication = useContext(AuthenticationContext);
  const initialReviewer =
    authentication?.session?.user.display_name ??
    authentication?.session?.user.email ??
    "";
  const [reviewer, setReviewer] = useState(initialReviewer);
  const [attestation, setAttestation] = useState(false);
  const [publications, setPublications] = useState(() =>
    initialLinkedPublications(document, imported),
  );
  const [warningExplanations, setWarningExplanations] = useState<
    Record<string, string>
  >({});
  const [correctionReason, setCorrectionReason] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [createdPump, setCreatedPump] = useState<PumpTemplateRead | null>(null);
  const [newPumpType, setNewPumpType] =
    useState<PumpRevisionInput["pump_type"] | "">("");
  const [partialCorrectionConfirmed, setPartialCorrectionConfirmed] =
    useState(false);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const dirtyRef = useRef(false);
  const actionBusyRef = useRef(false);
  const canVerifyRef = useRef(false);
  const correctionSeededRef = useRef(false);
  const publicationEditsRef = useRef(false);
  const onDirtyChangeRef = useRef(onDirtyChange);
  const resetSourceRef = useRef(
    `${imported.id}:${imported.updated_at}:${imported.state}`,
  );
  const warningCodes = uniqueWarningCodes(imported);
  const pumpSeed = extractedPumpSeed(imported);
  const trimVariantPlan = multiTrimVariantSeeds(document);
  const usesPerTrimVariantMapping = trimVariantPlan !== null;
  const trimVariantGroups = publicationVariantGroups(publications, document);
  const selectedTrimVariantIds = trimVariantGroups
    .map((group) => group.variantId)
    .filter((variantId) => variantId !== "");
  const trimVariantMappingsAreDistinct =
    !usesPerTrimVariantMapping ||
    (selectedTrimVariantIds.length === trimVariantGroups.length &&
      new Set(selectedTrimVariantIds).size === trimVariantGroups.length);
  const hasDuplicateTrimVariants =
    usesPerTrimVariantMapping &&
    new Set(selectedTrimVariantIds).size !== selectedTrimVariantIds.length;
  const pumpCreationMissing =
    trimVariantPlan === null
      ? pumpSeed.missing
      : [
          ...pumpSeed.missing.filter((item) => item !== "impeller size with units"),
          ...(trimVariantPlan.invalid ? ["trim labels with units"] : []),
        ];
  const trimReadiness = curveGroupReadiness(document);
  const readyTrimGroups = trimReadiness.filter(
    (group) => group.missingFamilies.length === 0,
  );
  const incompleteTrimGroups = trimReadiness.filter(
    (group) => group.missingFamilies.length > 0,
  );
  const otherFindings = imported.findings.filter(
    (finding) => !isContourIntersectionGap(finding),
  );

  useEffect(() => {
    onDirtyChangeRef.current = onDirtyChange;
  }, [onDirtyChange]);

  useEffect(() => {
    onBusyChange?.(busyAction !== null);
  }, [busyAction, onBusyChange]);

  useEffect(() => () => onBusyChange?.(false), [onBusyChange]);

  const markDirty = useCallback(() => {
    if (dirtyRef.current) return;
    dirtyRef.current = true;
    onDirtyChangeRef.current?.(true);
  }, []);

  const clearDirty = useCallback(() => {
    publicationEditsRef.current = false;
    if (!dirtyRef.current) return;
    dirtyRef.current = false;
    onDirtyChangeRef.current?.(false);
  }, []);

  useEffect(
    () => () => {
      clearDirty();
    },
    [clearDirty],
  );

  useEffect(() => {
    const importKey = `${imported.id}:${imported.updated_at}:${imported.state}`;
    if (resetSourceRef.current === importKey) return;
    resetSourceRef.current = importKey;
    correctionSeededRef.current = false;
    setReviewer(initialReviewer);
    setAttestation(false);
    setPublications(initialLinkedPublications(document, imported));
    setWarningExplanations({});
    setCreatedPump(null);
    setNewPumpType("");
    setCorrectionReason("");
    setRejectionReason("");
    setPartialCorrectionConfirmed(false);
    clearDirty();
  }, [clearDirty, document, imported, initialReviewer]);

  const revisionHistory = useQuery({
    queryFn: () => api.listRevisions(imported.id),
    queryKey: ["flow-wizard", "curve-import-revisions", imported.id],
  });
  const pumpTemplates = useQuery({
    queryFn: () => catalog.listPumps("", "exclude"),
    queryKey: ["flow-wizard", "curve-import-catalog-options"],
  });
  const sharedTemplateId =
    publications.length > 0 &&
    publications.every(
      (publication) =>
        publication.linkCatalog &&
        publication.catalogTemplateId ===
          publications[0]?.catalogTemplateId,
    )
      ? (publications[0]?.catalogTemplateId ?? "")
      : "";
  const sharedVariantId =
    sharedTemplateId !== "" &&
    publications.every(
      (publication) =>
        publication.catalogVariantId === publications[0]?.catalogVariantId,
    )
      ? (publications[0]?.catalogVariantId ?? "")
      : "";
  const selectedSharedPump = useQuery({
    enabled: sharedTemplateId !== "",
    queryFn: () => catalog.getPump(sharedTemplateId),
    queryKey: [
      "flow-wizard",
      "curve-import-shared-catalog-template",
      sharedTemplateId,
    ],
  });
  const selectedSharedPumpData =
    createdPump?.id === sharedTemplateId
      ? createdPump
      : selectedSharedPump.data;
  const sharedVariants = catalogVariants(
    selectedSharedPumpData?.current_revision.variants,
  );

  const chooseSharedTemplate = (templateId: string) => {
    const selected = pumpTemplates.data?.items.find(
      (template) => template.id === templateId,
    );
    const revisionId =
      selected?.revision_id ??
      (createdPump?.id === templateId ? createdPump.current_revision.id : "");
    publicationEditsRef.current = true;
    markDirty();
    setPublications((current) =>
      current.map((publication) => ({
        ...publication,
        catalogRevisionId: revisionId,
        catalogTemplateId: templateId,
        catalogVariantId: "",
        linkCatalog: true,
      })),
    );
  };

  const chooseSharedVariant = (variantId: string) => {
    publicationEditsRef.current = true;
    markDirty();
    setPublications((current) =>
      current.map((publication) => ({
        ...publication,
        catalogVariantId: variantId,
        linkCatalog: true,
      })),
    );
  };

  const chooseTrimVariant = (
    publicationIds: string[],
    variantId: string,
  ) => {
    const selectedIds = new Set(publicationIds);
    publicationEditsRef.current = true;
    markDirty();
    setPublications((current) =>
      current.map((publication) =>
        selectedIds.has(publication.id)
          ? { ...publication, catalogVariantId: variantId, linkCatalog: true }
          : publication,
      ),
    );
  };

  const createPumpFromSheet = async () => {
    if (disabled || busyAction !== null || actionBusyRef.current) return;
    if (trimVariantPlan?.invalid === true) return;
    const trimVariants = trimVariantPlan?.seeds ?? null;
    const payload = pumpCreatePayload(
      imported,
      pumpSeed,
      newPumpType,
      trimVariants,
    );
    if (payload === null) return;
    actionBusyRef.current = true;
    setBusyAction("create-pump");
    try {
      const created = await catalog.createPump(payload);
      setCreatedPump(created);
      const createdVariants = catalogVariants(
        created.current_revision.variants,
      );
      const variantIdsByTrim = new Map<string, string>();
      if (trimVariants !== null) {
        for (const trim of trimVariants) {
          const variant = variantForTrim(
            createdVariants,
            trim.label,
            trim.diameterM,
          );
          if (variant !== null) {
            variantIdsByTrim.set(trimIdentity(trim.label), variant.id);
          }
        }
      }
      const sharedCreatedVariant =
        trimVariants === null ? (createdVariants[0] ?? null) : null;
      publicationEditsRef.current = true;
      markDirty();
      setPublications((current) =>
        current.map((publication) => {
          const trim = cleanText(
            publicationHead(publication, document)?.trim_label,
          );
          const variantId =
            trimVariants === null
              ? (sharedCreatedVariant?.id ?? "")
              : trim === null
                ? ""
                : (variantIdsByTrim.get(trimIdentity(trim)) ?? "");
          return {
            ...publication,
            catalogRevisionId: created.current_revision.id,
            catalogTemplateId: created.id,
            catalogVariantId: variantId,
            linkCatalog: true,
          };
        }),
      );
      const everyTrimMatched =
        trimVariants === null
          ? sharedCreatedVariant !== null
          : trimVariants.every((trim) =>
              variantIdsByTrim.has(trimIdentity(trim.label)),
            );
      if (everyTrimMatched) {
        onNotice("Pump created and selected.");
      } else {
        onError("Pump created. Match each trim before saving.");
      }
      void pumpTemplates.refetch();
    } catch (error: unknown) {
      onError(readableCurveError(error, "Pump could not be created."));
    } finally {
      actionBusyRef.current = false;
      setBusyAction(null);
    }
  };

  useEffect(() => {
    const revisions = revisionHistory.data;
    if (
      imported.state !== "needs_review" ||
      revisions === undefined ||
      correctionSeededRef.current ||
      publicationEditsRef.current
    ) {
      return;
    }
    correctionSeededRef.current = true;
    setPartialCorrectionConfirmed(false);
    if (revisions.length > 0) {
      setPublications(seedCorrectionPublications(revisions));
    }
  }, [imported.state, revisionHistory.data]);

  const updatePublication = (id: string, next: PublicationDraft) => {
    publicationEditsRef.current = true;
    markDirty();
    setPublications((current) =>
      current.map((publication) =>
        publication.id === id ? next : publication,
      ),
    );
  };

  const addPublication = () => {
    publicationEditsRef.current = true;
    markDirty();
    const baseName =
      imported.suggestions.model ??
      imported.suggestions.curve_number ??
      "Pump curve";
    setPublications((current) => {
      const next = createPublicationDraft(
        `${baseName} — set ${current.length + 1}`,
        imported.suggestions.curve_revision ?? "A",
        imported.suggestions.speeds_rpm[0] ?? null,
        [],
      );
      return [...current, { ...next, linkCatalog: true }];
    });
  };

  const keepReadyTrims = async () => {
    if (
      disabled ||
      hasUnsavedChanges ||
      readyTrimGroups.length === 0 ||
      incompleteTrimGroups.length === 0 ||
      busyAction !== null ||
      actionBusyRef.current
    ) {
      return;
    }
    if (
      dirtyRef.current &&
      !window.confirm(
        "Keep the ready trims and discard unchecked pump setup changes?",
      )
    ) {
      return;
    }
    actionBusyRef.current = true;
    setBusyAction("keep-ready-trims");
    try {
      const next = await api.updateDraft(
        imported.id,
        pruneIncompleteCurveGroups(document),
      );
      clearDirty();
      onImported(next);
      onNotice(
        `Using ${readyTrimGroups.length} complete trim${readyTrimGroups.length === 1 ? "" : "s"}. Choose the pump and save.`,
      );
    } catch (error: unknown) {
      onError(readableCurveError(error, "Ready trims could not be kept."));
    } finally {
      actionBusyRef.current = false;
      setBusyAction(null);
    }
  };

  const submitVerification = async (event: FormEvent) => {
    event.preventDefault();
    if (!canVerifyRef.current || busyAction !== null || actionBusyRef.current)
      return;
    const payload: VerifyImportInput = {
      attestation,
      publications: publications.map(publicationPayload),
      reviewer,
      warning_acceptances: warningCodes.map((findingCode) => ({
        explanation: warningExplanations[findingCode] ?? "",
        finding_code: findingCode,
      })),
    };
    actionBusyRef.current = true;
    setBusyAction("verify");
    try {
      const result = await api.verifyImport(imported.id, payload);
      const eligibleCount = result.revisions.filter(
        (revision) => revision.catalog_linked && revision.optimization_eligible,
      ).length;
      clearDirty();
      onImported(result.imported);
      onNotice(
        `${result.revisions.length} immutable curve revision${result.revisions.length === 1 ? "" : "s"} verified. ` +
          (eligibleCount > 0
            ? `${eligibleCount} ${eligibleCount === 1 ? "is" : "are"} ready for planning.`
            : "Saved as a checked reference; it is not linked for normal planning."),
      );
      await revisionHistory.refetch();
    } catch (error: unknown) {
      onError(readableCurveError(error, "Verification failed."));
    } finally {
      actionBusyRef.current = false;
      setBusyAction(null);
    }
  };

  const beginCorrection = async () => {
    if (disabled || busyAction !== null || actionBusyRef.current) return;
    actionBusyRef.current = true;
    setBusyAction("correction");
    try {
      const next = await api.beginCorrection(imported.id, correctionReason);
      clearDirty();
      onImported(next);
      onNotice(
        "Correction draft opened. Earlier verified revisions are unchanged.",
      );
    } catch (error: unknown) {
      onError(readableCurveError(error, "Correction could not be opened."));
    } finally {
      actionBusyRef.current = false;
      setBusyAction(null);
    }
  };

  const reject = async () => {
    if (disabled || busyAction !== null || actionBusyRef.current) return;
    actionBusyRef.current = true;
    setBusyAction("reject");
    try {
      const next = await api.rejectImport(imported.id, rejectionReason);
      clearDirty();
      onImported(next);
      onNotice("Import marked as rejected for insufficient source material.");
    } catch (error: unknown) {
      onError(readableCurveError(error, "Import could not be rejected."));
    } finally {
      actionBusyRef.current = false;
      setBusyAction(null);
    }
  };

  const correctionMode = (revisionHistory.data?.length ?? 0) > 0;
  const latestRevisionByCurveSet = new Map<string, CurveSetRevisionRead>();
  for (const revision of revisionHistory.data ?? []) {
    const current = latestRevisionByCurveSet.get(revision.curve_set_id);
    if (
      current === undefined ||
      revision.revision_number > current.revision_number
    ) {
      latestRevisionByCurveSet.set(revision.curve_set_id, revision);
    }
  }
  const retainedCurveSetIds = new Set(
    publications.flatMap((publication) =>
      publication.curveSetId === null ? [] : [publication.curveSetId],
    ),
  );
  const removedHistoricalTraceIds = new Set(
    [...latestRevisionByCurveSet.values()]
      .filter((revision) => !retainedCurveSetIds.has(revision.curve_set_id))
      .flatMap((revision) => revision.measured_traces.map((trace) => trace.id)),
  );
  const traceAssignmentCounts = new Map<string, number>();
  for (const publication of publications) {
    for (const traceId of publication.measuredTraceIds) {
      traceAssignmentCounts.set(
        traceId,
        (traceAssignmentCounts.get(traceId) ?? 0) + 1,
      );
    }
  }
  const traceAssignmentsValid =
    document.traces.length > 0 &&
    document.traces.every((trace) => {
      const assignmentCount = traceAssignmentCounts.get(trace.id) ?? 0;
      return (
        assignmentCount === 1 ||
        (traceCanBeShared(trace) && assignmentCount > 1) ||
        (!isSourceContour(trace) && correctionMode &&
          partialCorrectionConfirmed &&
          assignmentCount === 0 &&
          removedHistoricalTraceIds.has(trace.id))
      );
    });
  const publicationNames = publications.map((publication) =>
    normalizeCurveIdentity(publication.name),
  );
  const namesAreUnique =
    publicationNames.length === new Set(publicationNames).size;
  const warningAcceptancesComplete = warningCodes.every((code) => {
    const length = (warningExplanations[code] ?? "").trim().length;
    return length >= 3 && length <= 2000;
  });
  const reviewerComplete =
    reviewer.trim().length > 0 && reviewer.trim().length <= 200;
  const existingCurveNames = new Set(
    (revisionHistory.data ?? []).map((revision) =>
      normalizeCurveIdentity(revision.curve_set_name),
    ),
  );
  const correctionIdentityLoaded =
    revisionHistory.data !== undefined &&
    (revisionHistory.data.length === 0 || correctionSeededRef.current);
  const documentTraceIds = new Set(document.traces.map((trace) => trace.id));
  const correctionHasStaleTraces = publications.some(
    (publication) =>
      publication.curveSetId !== null &&
      publication.measuredTraceIds.some(
        (traceId) => !documentTraceIds.has(traceId),
      ),
  );
  const correctionIdentitiesSafe =
    correctionIdentityLoaded &&
    publications.every(
      (publication) =>
        publication.curveSetId !== null ||
        !existingCurveNames.has(normalizeCurveIdentity(publication.name)),
    );
  const correctionRevisionLabelsUnique = publications.every(
    (publication) =>
      publication.curveSetId === null ||
      (revisionHistory.data ?? []).some(
        (revision) =>
          revision.curve_set_id === publication.curveSetId &&
          normalizeCurveIdentity(revision.revision_label) ===
            normalizeCurveIdentity(publication.revisionLabel),
      ) === false,
  );
  const correctionEngineeringChangesComplete = publications.every(
    (publication) => {
      if (publication.curveSetId === null) return true;
      const priorRevisions = (revisionHistory.data ?? []).filter(
        (revision) => revision.curve_set_id === publication.curveSetId,
      );
      return (
        priorRevisions.length > 0 &&
        priorRevisions.every((revision) =>
          publicationEngineeringChanged(publication, document, revision),
        )
      );
    },
  );
  const publicationsAreReady =
    publications.length > 0 &&
    publications.every(
      (publication) =>
        publicationReady(publication) &&
        publicationMatchesDocument(publication, document),
    );
  const hasBlockingFindings = imported.findings.some(
    (finding) => finding.blocking,
  );
  const hasIncompleteContourGroups =
    imported.state === "needs_review" &&
    incompleteTrimGroups.length > 0;
  const canVerify =
    !disabled &&
    !hasUnsavedChanges &&
    !hasBlockingFindings &&
    !hasIncompleteContourGroups &&
    imported.state === "needs_review" &&
    attestation &&
    reviewerComplete &&
    warningAcceptancesComplete &&
    correctionIdentitiesSafe &&
    correctionRevisionLabelsUnique &&
    publicationsAreReady &&
    trimVariantMappingsAreDistinct &&
    correctionEngineeringChangesComplete &&
    namesAreUnique &&
    traceAssignmentsValid;
  canVerifyRef.current = canVerify;

  const tracesById = new Map(document.traces.map((trace) => [trace.id, trace]));
  const assignedHeadTraces = publications.flatMap((publication) =>
    publication.measuredTraceIds.flatMap((traceId) => {
      const trace = tracesById.get(traceId);
      return trace?.kind === "head" ? [trace] : [];
    }),
  );
  const singleHeadTrace =
    publications.length === 1 && assignedHeadTraces.length === 1
      ? (assignedHeadTraces[0] ?? null)
      : null;
  const headPoints =
    singleHeadTrace?.points.flatMap((point) =>
      point.source === null ? [] : [point.source],
    ) ?? [];
  const headCalibration =
    singleHeadTrace?.calibration_override ?? imported.calibration;
  const headCoverage =
    usesPerTrimVariantMapping
      ? `${trimVariantGroups.length} checked trim${trimVariantGroups.length === 1 ? "" : "s"}`
      : singleHeadTrace !== null &&
    headPoints.length >= 2 &&
    headCalibration !== null
      ? `${rangeText(headPoints.map((point) => point.x))} ${headCalibration.x_axis.unit} flow · ${rangeText(headPoints.map((point) => point.y))} ${headCalibration.y_axis.unit} head`
      : `${assignedHeadTraces.length} checked head curve${assignedHeadTraces.length === 1 ? "" : "s"} across ${publications.length} speed${publications.length === 1 ? "" : "s"}`;
  const powerSetupCount = publications.filter((publication) =>
    publication.measuredTraceIds.some((traceId) => {
      const kind = tracesById.get(traceId)?.kind;
      return kind === "efficiency" || kind === "brake_power";
    }),
  ).length;
  const npshrSetupCount = publications.filter((publication) =>
    publication.measuredTraceIds.some(
      (traceId) => tracesById.get(traceId)?.kind === "npshr",
    ),
  ).length;
  const powerCoverage =
    powerSetupCount === 0
      ? "Needs efficiency or brake power data"
      : powerSetupCount === publications.length
        ? publications.length === 1
          ? "Data included"
          : `Data included for all ${publications.length} ${usesPerTrimVariantMapping ? "trims" : "speeds"}`
        : `Data included for ${powerSetupCount} of ${publications.length} ${usesPerTrimVariantMapping ? "trims" : "speeds"}`;
  const npshrCoverage =
    npshrSetupCount === 0
      ? "Needs NPSHr data"
      : npshrSetupCount === publications.length
        ? publications.length === 1
          ? "NPSHr data included"
          : `NPSHr included for all ${publications.length} ${usesPerTrimVariantMapping ? "trims" : "speeds"}`
        : `NPSHr included for ${npshrSetupCount} of ${publications.length} ${usesPerTrimVariantMapping ? "trims" : "speeds"}`;
  const planningEligibleRevision =
    revisionHistory.data?.some(
      (revision) => revision.catalog_linked && revision.optimization_eligible,
    ) ?? false;
  const revisionEligibilityKnown = revisionHistory.data !== undefined;
  const revisionEligibilityUnavailable =
    revisionHistory.isError && !revisionEligibilityKnown;

  return (
    <div className="curve-review-stack">
      {imported.findings.length === 0 && !hasIncompleteContourGroups ? null : (
        <section
          className="curve-findings"
          aria-labelledby="curve-findings-title"
        >
          <div className="curve-section-heading">
            <div>
              <h3 id="curve-findings-title" tabIndex={-1}>
                Curve check
              </h3>
            </div>
          </div>
          {hasIncompleteContourGroups ? (
            <div className="curve-trim-readiness">
              <p className="curve-trim-readiness__ready">
                <strong>
                  {readyTrimGroups.length} trim
                  {readyTrimGroups.length === 1 ? "" : "s"} ready
                </strong>
                {readyTrimGroups.length === 0
                  ? null
                  : `: ${readyTrimGroups
                      .map((group) => curveGroupLabel(group.head))
                      .join(", ")}`}
              </p>
              <ul aria-label="Incomplete impeller trims">
                {incompleteTrimGroups.map((group) => (
                  <li key={group.head.id}>
                    <strong>{curveGroupLabel(group.head)}</strong>
                    <span>
                      Needs{" "}
                      {group.missingFamilies
                        .map(curveFamilyLabel)
                        .join(", ")
                        .replace(/, ([^,]*)$/u, " and $1")}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="curve-button-row">
                {readyTrimGroups.length === 0 ? null : (
                  <button
                    className="curve-primary-action"
                    disabled={disabled || hasUnsavedChanges || busyAction !== null}
                    type="button"
                    onClick={() => void keepReadyTrims()}
                  >
                    {busyAction === "keep-ready-trims"
                      ? "Using…"
                      : `Use ${readyTrimGroups.length} complete trim${readyTrimGroups.length === 1 ? "" : "s"}`}
                  </button>
                )}
                {onCorrect === undefined ? null : (
                  <button
                    className="button button--secondary"
                    type="button"
                    onClick={onCorrect}
                  >
                    Open curve editor
                  </button>
                )}
              </div>
            </div>
          ) : hasBlockingFindings && onCorrect !== undefined ? (
            <div className="curve-findings-next-action">
              <p>Fix traced lines, curve types, or axes.</p>
              <button type="button" onClick={onCorrect}>
                Open curve editor
              </button>
            </div>
          ) : null}
          {otherFindings.length === 0 ? null : (
            <ul className="curve-finding-list">
            {otherFindings.map((finding, index) => (
              <li
                key={finding.code + ":" + index}
                className={"is-" + finding.severity}
              >
                <span>{finding.message}</span>
                <small>
                  {finding.severity === "warning"
                    ? "Confirm below"
                    : finding.blocking
                      ? "Fix this before saving"
                      : "Review before saving"}
                </small>
                <details className="curve-finding-details">
                  <summary
                    aria-label={`Technical details for ${finding.message}`}
                  >
                    Technical details
                  </summary>
                  <dl>
                    <div>
                      <dt>Code</dt>
                      <dd>
                        <code>{finding.code}</code>
                      </dd>
                    </div>
                    <div>
                      <dt>Location</dt>
                      <dd>
                        <code>
                          {finding.location.length === 0
                            ? "Source document"
                            : finding.location.join(" → ")}
                        </code>
                      </dd>
                    </div>
                  </dl>
                </details>
              </li>
            ))}
          </ul>
          )}
        </section>
      )}

      {imported.state === "needs_review" ? (
        <form
          className="curve-verification curve-simple-review"
          noValidate
          onSubmit={(event) => void submitVerification(event)}
        >
          <fieldset
            className="curve-review-controls"
            disabled={
              disabled || busyAction !== null || !correctionIdentityLoaded
            }
          >
            <div className="curve-simple-heading">
              <div>
                <h2 id="curve-save-title" tabIndex={-1}>
                  Choose the pump and save
                </h2>
              </div>
            </div>

            {correctionMode ? null : (
              <div className="curve-pump-selection">
              <div
                className="curve-shared-pump-choice"
              >
                <label>
                  Pump
                  <select
                    {...guided(
                      curveImportFieldHelp.catalogTemplate,
                      imported.id + ":shared",
                    )}
                    required
                    aria-label={
                      usesPerTrimVariantMapping
                        ? "Pump for all detected trims"
                        : "Pump for all detected speeds"
                    }
                    value={sharedTemplateId}
                    onChange={(event) =>
                      chooseSharedTemplate(event.target.value)
                    }
                  >
                    <option value="">Choose a pump</option>
                    {pumpTemplates.data?.items.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.template_name} — {template.manufacturer}{" "}
                        {template.model}
                      </option>
                    ))}
                    {createdPump !== null &&
                    !pumpTemplates.data?.items.some(
                      (template) => template.id === createdPump.id,
                    ) ? (
                      <option value={createdPump.id}>
                        {createdPump.template_name}
                      </option>
                    ) : null}
                  </select>
                </label>
                {usesPerTrimVariantMapping ? (
                  <fieldset className="curve-trim-variant-map">
                    <legend>Match each trim</legend>
                    {trimVariantGroups.map((group) => (
                      <label key={group.key}>
                        <span>{group.label}</span>
                        <select
                          {...guided(
                            curveImportFieldHelp.catalogVariant,
                            imported.id + ":" + group.key,
                          )}
                          required
                          aria-label={`Variant for ${group.label}`}
                          disabled={selectedSharedPumpData === undefined}
                          value={group.variantId}
                          onChange={(event) =>
                            chooseTrimVariant(
                              group.publicationIds,
                              event.target.value,
                            )
                          }
                        >
                          <option value="">Choose a variant</option>
                          {sharedVariants.map((variant) => (
                            <option
                              key={variant.id}
                              disabled={trimVariantGroups.some(
                                (other) =>
                                  other.key !== group.key &&
                                  other.variantId === variant.id,
                              )}
                              value={variant.id}
                            >
                              {variant.name}
                            </option>
                          ))}
                        </select>
                      </label>
                    ))}
                  </fieldset>
                ) : (
                  <label>
                    Impeller / variant
                    <select
                      {...guided(
                        curveImportFieldHelp.catalogVariant,
                        imported.id + ":shared",
                      )}
                      required
                      aria-label="Impeller or variant for all detected speeds"
                      disabled={selectedSharedPumpData === undefined}
                      value={sharedVariantId}
                      onChange={(event) =>
                        chooseSharedVariant(event.target.value)
                      }
                    >
                      <option value="">Choose a pump variant</option>
                      {sharedVariants.map((variant) => (
                        <option key={variant.id} value={variant.id}>
                          {variant.name}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
              <details className="curve-create-pump">
                <summary>Create pump from this sheet</summary>
                {pumpCreationMissing.length > 0 ? (
                  <p>Needs {pumpCreationMissing.join(", ")}.</p>
                ) : (
                  <div className="curve-create-pump__body">
                    <dl aria-label="New pump values">
                      <div>
                        <dt>Pump</dt>
                        <dd>
                          {pumpSeed.manufacturer} {pumpSeed.model}
                        </dd>
                      </div>
                      <div>
                        <dt>Revision</dt>
                        <dd>{pumpSeed.revisionLabel}</dd>
                      </div>
                      <div>
                        <dt>Size</dt>
                        <dd>{pumpSeed.pumpSizeText ?? "Not found"}</dd>
                      </div>
                      <div>
                        <dt>Suction</dt>
                        <dd>{pumpSeed.suctionText}</dd>
                      </div>
                      <div>
                        <dt>Discharge</dt>
                        <dd>{pumpSeed.dischargeText}</dd>
                      </div>
                      <div>
                        <dt>
                          {trimVariantPlan === null ? "Impeller" : "Trims"}
                        </dt>
                        <dd>
                          {trimVariantPlan === null
                            ? (pumpSeed.impellerText ?? "Not found")
                            : trimVariantPlan.seeds
                                .map((variant) => variant.label)
                                .join(", ")}
                        </dd>
                      </div>
                    </dl>
                    <label>
                      Pump type
                      <select
                        {...guided(
                          curveImportFieldHelp.newPumpType,
                          imported.id + ":new-pump",
                        )}
                        aria-label="Pump type for new pump"
                        value={newPumpType}
                        onChange={(event) =>
                          setNewPumpType(
                            event.target
                              .value as PumpRevisionInput["pump_type"] | "",
                          )
                        }
                      >
                        <option value="centrifugal">Centrifugal</option>
                        <option value="">Choose type</option>
                        <option value="positive_displacement">
                          Positive displacement
                        </option>
                        <option value="other">Other</option>
                      </select>
                    </label>
                    {createdPump === null ? (
                      <button
                        type="button"
                        disabled={newPumpType === ""}
                        onClick={() => void createPumpFromSheet()}
                      >
                        {busyAction === "create-pump"
                          ? "Creating…"
                          : "Create draft pump"}
                      </button>
                    ) : (
                      <p>Pump created and selected.</p>
                    )}
                    <small>Creates a draft catalog pump.</small>
                  </div>
                )}
              </details>
              </div>
            )}

            <dl
              className="curve-coverage-summary"
              aria-label="Available pump data"
            >
              <div>
                <dt>Flow and head</dt>
                <dd>
                  {headCoverage}
                  {singleHeadTrace?.speed_rpm === null ||
                  singleHeadTrace?.speed_rpm === undefined
                    ? ""
                    : " at " + singleHeadTrace.speed_rpm + " RPM"}
                </dd>
              </div>
              <div>
                <dt>Power check</dt>
                <dd>{powerCoverage}</dd>
              </div>
              <div>
                <dt>Suction check</dt>
                <dd>{npshrCoverage}</dd>
              </div>
            </dl>

            {hasUnsavedChanges ? (
              <p className="curve-verification-gate" role="status">
                Check your edited values before saving.
              </p>
            ) : null}
            {hasBlockingFindings || hasIncompleteContourGroups ? (
              <p className="curve-verification-gate" role="status">
                Complete the curve check above.
              </p>
            ) : null}
            {traceAssignmentsValid === false ? (
              <p className="curve-verification-gate" role="status">
                {correctionMode && !partialCorrectionConfirmed
                  ? "To leave an unchanged pump setup at its prior revision, remove that setup and confirm the partial correction under advanced publishing options."
                  : "Some curve values still need one speed. Open advanced publishing options."}
              </p>
            ) : null}
            {hasDuplicateTrimVariants ? (
              <p className="curve-verification-gate" role="status">
                Each trim needs a different pump variant.
              </p>
            ) : null}
            {!namesAreUnique ? (
              <p className="curve-verification-gate" role="status">
                Each {usesPerTrimVariantMapping ? "trim" : "speed"} needs a
                different internal name.
              </p>
            ) : null}
            {!correctionIdentityLoaded ? (
              <p className="curve-verification-gate" role="status">
                {revisionHistory.isError
                  ? "Saved curve history could not be loaded. Reload before saving."
                  : "Loading saved curve history before this curve can be saved."}
              </p>
            ) : null}
            {correctionIdentityLoaded && !correctionIdentitiesSafe ? (
              <p className="curve-verification-gate" role="status">
                A saved curve name is missing its immutable identity. Reload
                before saving.
              </p>
            ) : null}
            {correctionRevisionLabelsUnique === false ? (
              <p className="curve-verification-gate" role="status">
                Use a new revision label that has not already been saved for
                this pump setup.
              </p>
            ) : null}
            {correctionMode &&
            correctionEngineeringChangesComplete === false ? (
              <p className="curve-verification-gate" role="status">
                Change at least one engineering value in every corrected pump
                setup, or remove an unchanged setup below. A new label,
                reviewer, or catalog link alone does not create a new curve
                revision.
              </p>
            ) : null}
            {correctionHasStaleTraces ? (
              <p className="curve-verification-gate" role="status">
                A prior pump setup refers to curve values that are no longer in
                this draft.
              </p>
            ) : null}

            <div className="curve-publication-list">
              {publications.map((publication, index) => {
                const otherTraceIds = new Set(
                  publications
                    .filter((candidate) => candidate.id !== publication.id)
                    .flatMap((candidate) => candidate.measuredTraceIds)
                    .filter((traceId) => {
                      const trace = tracesById.get(traceId);
                      return trace === undefined || !traceCanBeShared(trace);
                    }),
                );
                return (
                  <PublicationEditor
                    key={publication.id}
                    catalog={catalog}
                    showCatalogChoice={correctionMode}
                    document={document}
                    index={index}
                    otherTraceIds={otherTraceIds}
                    groupLabel={
                      correctionMode
                        ? null
                        : publicationGroupLabel(
                            publication,
                            document,
                            index,
                            usesPerTrimVariantMapping,
                          )
                    }
                    publication={publication}
                    pumpTemplates={pumpTemplates.data?.items ?? []}
                    removable={publications.length > 1}
                    onChange={(next) => updatePublication(publication.id, next)}
                    onRemove={() => {
                      publicationEditsRef.current = true;
                      markDirty();
                      setPublications((current) =>
                        current.filter(
                          (candidate) => candidate.id !== publication.id,
                        ),
                      );
                    }}
                  />
                );
              })}
            </div>

            <label className="curve-simple-reviewer">
              Checked by
              <input
                {...guided(curveImportFieldHelp.reviewer)}
                required
                maxLength={200}
                value={reviewer}
                onChange={(event) => {
                  markDirty();
                  setReviewer(event.target.value);
                }}
              />
            </label>

            {warningCodes.length === 0 ? null : (
              <fieldset className="curve-warning-review">
                <legend>
                  {warningCodes.length === 1
                    ? "Confirm warning"
                    : "Confirm warnings"}
                </legend>
                {warningCodes.map((code) => (
                  <label key={code}>
                    {imported.findings.find((finding) => finding.code === code)
                      ?.message ?? code}
                    <span className="curve-warning-prompt">
                      What did you check?
                    </span>
                    <textarea
                      {...guided(curveImportFieldHelp.warningAcceptance, code)}
                      required
                      maxLength={2000}
                      rows={2}
                      value={warningExplanations[code] ?? ""}
                      onChange={(event) => {
                        markDirty();
                        setWarningExplanations({
                          ...warningExplanations,
                          [code]: event.target.value,
                        });
                      }}
                    />
                  </label>
                ))}
              </fieldset>
            )}

            <label className="curve-attestation">
              <input
                {...guided(curveImportFieldHelp.verificationAttestation)}
                checked={attestation}
                type="checkbox"
                onChange={(event) => {
                  markDirty();
                  setAttestation(event.target.checked);
                }}
              />
              I compared the source and checked the pump, units, RPM, curves,
              limits, and warnings.
            </label>

            <button
              className="curve-save-primary"
              disabled={!canVerify || busyAction !== null}
              type="submit"
            >
              {busyAction === "verify"
                ? "Saving…"
                : publications.every((publication) => publication.linkCatalog)
                  ? "Save curve for planning"
                  : "Save checked reference"}
            </button>

            <details className="curve-publishing-advanced">
              <summary>Advanced publishing options</summary>
              <button type="button" onClick={addPublication}>
                Add speed or impeller
              </button>
              {correctionMode ? (
                <div className="curve-partial-correction">
                  <p className="curve-help">
                    Remove any unchanged pump setup above. Its last saved
                    revision will remain available.
                  </p>
                  <label className="curve-inline-check">
                    <input
                      {...guided(
                        curveImportFieldHelp.partialCorrection,
                        imported.id,
                      )}
                      checked={partialCorrectionConfirmed}
                      type="checkbox"
                      onChange={(event) => {
                        publicationEditsRef.current = true;
                        markDirty();
                        setPartialCorrectionConfirmed(event.target.checked);
                      }}
                    />
                    Only publish the pump setups still listed above; leave every
                    removed setup at its prior saved revision. I understand that
                    draft edits to values from a removed setup will not be
                    published.
                  </label>
                </div>
              ) : null}
            </details>
          </fieldset>
        </form>
      ) : null}

      {imported.state === "verified" ? (
        <section className="curve-saved-summary">
          <h2>
            {revisionEligibilityUnavailable
              ? "Curve saved; planning status unavailable"
              : !revisionEligibilityKnown
                ? "Curve saved"
                : planningEligibleRevision
                  ? "Ready to compare against projects"
                  : "Saved as a checked reference"}
          </h2>
          <p
            className={
              revisionEligibilityUnavailable ? "curve-alert" : undefined
            }
            role={revisionEligibilityUnavailable ? "alert" : undefined}
          >
            {revisionEligibilityUnavailable
              ? "Saved history could not be loaded. Reload this page before relying on planning eligibility."
              : !revisionEligibilityKnown
                ? "Checking whether the saved revision is linked and eligible for planning."
                : planningEligibleRevision
                  ? "A catalog-linked revision is eligible for planning. Missing power, efficiency, or NPSHr data will still be called out when a project needs it."
                  : "No saved revision from this source is currently catalog-linked and planning-eligible."}
          </p>
        </section>
      ) : null}

      <details className="curve-history-disclosure">
        <summary>History ({revisionHistory.data?.length ?? 0})</summary>
        <section
          className="curve-revision-history"
          aria-labelledby="curve-history-title"
        >
          <h3 id="curve-history-title">Saved revisions</h3>
          {revisionHistory.isLoading ? <p>Loading history…</p> : null}
          {revisionHistory.isError ? (
            <p className="curve-alert" role="alert">
              Saved revision history could not be loaded. Reload this page to
              try again.
            </p>
          ) : null}
          <ol>
            {revisionHistory.data?.map((revision) => (
              <li key={revision.id}>
                <strong>
                  {revision.curve_set_name} · {revision.revision_label}
                </strong>
                <span>
                  Revision {revision.revision_number} ·{" "}
                  {revision.tested_speed_rpm} RPM ·{" "}
                  {revision.catalog_linked ? "Catalog linked" : "Standalone"} ·{" "}
                  {revision.catalog_linked && revision.optimization_eligible
                    ? "Planning eligible"
                    : "Reference only"}
                </span>
                <small>Checked by {revision.reviewer}</small>
              </li>
            ))}
          </ol>
        </section>
      </details>

      {canReviewCurveLibrary(authentication) && imported.state === "verified" ? (
        <details className="curve-more-actions">
          <summary>Internal review</summary>
          <CurveLibrarySubmissionPanel
            api={library}
            imported={imported}
            authenticated
            disabled={disabled || busyAction !== null || hasUnsavedChanges}
            onBusyChange={onBusyChange}
          />
        </details>
      ) : null}

      <details className="curve-more-actions">
        <summary>More actions</summary>
        {imported.state === "verified" ? (
          <section
            className="curve-correction"
            aria-labelledby="curve-correction-title"
          >
            <h3 id="curve-correction-title">Correct this saved curve</h3>
            <p>This opens a new draft. The saved revision remains unchanged.</p>
            <label>
              Why is a correction needed?
              <textarea
                {...guided(curveImportFieldHelp.correctionReason)}
                disabled={disabled || busyAction !== null}
                maxLength={2000}
                rows={3}
                value={correctionReason}
                onChange={(event) => {
                  markDirty();
                  setCorrectionReason(event.target.value);
                }}
              />
            </label>
            <button
              disabled={
                disabled ||
                correctionReason.trim().length < 3 ||
                busyAction !== null
              }
              type="button"
              onClick={() => void beginCorrection()}
            >
              Start correction
            </button>
          </section>
        ) : null}

        {imported.state !== "verified" &&
        imported.state !== "rejected_insufficient" ? (
          <section
            className="curve-rejection"
            aria-labelledby="curve-rejection-title"
          >
            <h3 id="curve-rejection-title">This sheet cannot be used</h3>
            <label>
              Reason
              <textarea
                {...guided(curveImportFieldHelp.rejectionReason)}
                disabled={disabled || busyAction !== null}
                maxLength={2000}
                rows={3}
                value={rejectionReason}
                onChange={(event) => {
                  markDirty();
                  setRejectionReason(event.target.value);
                }}
              />
            </label>
            <button
              className="curve-danger"
              disabled={
                disabled ||
                rejectionReason.trim().length < 3 ||
                busyAction !== null
              }
              type="button"
              onClick={() => void reject()}
            >
              Mark sheet as unusable
            </button>
          </section>
        ) : null}
      </details>
    </div>
  );
}

function seedCorrectionPublications(
  revisions: CurveSetRevisionRead[],
): PublicationDraft[] {
  const latestByCurveSet = new Map<string, CurveSetRevisionRead>();
  for (const revision of revisions) {
    const current = latestByCurveSet.get(revision.curve_set_id);
    if (
      current === undefined ||
      revision.revision_number > current.revision_number
    ) {
      latestByCurveSet.set(revision.curve_set_id, revision);
    }
  }
  const available = [...latestByCurveSet.values()].sort((left, right) =>
    left.curve_set_name.localeCompare(right.curve_set_name),
  );
  const numberString = (value: number | null | undefined) =>
    value === null || value === undefined ? "" : String(value);

  return available.map((selected) => {
    const publication = createPublicationDraft(
      selected.curve_set_name,
      "",
      selected.tested_speed_rpm,
      selected.measured_traces.map((trace) => trace.id),
    );
    const usedLabels = new Set(
      revisions
        .filter((revision) => revision.curve_set_id === selected.curve_set_id)
        .map((revision) => normalizeCurveIdentity(revision.revision_label)),
    );
    let nextNumber = selected.revision_number + 1;
    let nextLabel = "Correction " + nextNumber;
    while (usedLabels.has(normalizeCurveIdentity(nextLabel))) {
      nextNumber += 1;
      nextLabel = "Correction " + nextNumber;
    }
    return {
      ...publication,
      affinityRows: selected.affinity_derived_traces.flatMap((trace) =>
        trace.derived_from_trace_id === null ||
        trace.derived_from_trace_id === undefined ||
        trace.speed_rpm === null ||
        trace.speed_rpm === undefined
          ? []
          : [
              {
                sourceTraceId: trace.derived_from_trace_id,
                targetSpeedRpm: trace.speed_rpm,
              },
            ],
      ),
      bepFlow: numberString(selected.operating_envelope.bep_flow_m3_s),
      curveSetId: selected.curve_set_id,
      impellerDiameter: numberString(selected.impeller_diameter_m),
      linkCatalog: selected.catalog_linked,
      maximumFlow: numberString(selected.operating_envelope.maximum_flow_m3_s),
      maximumHead: numberString(selected.operating_envelope.maximum_head_m),
      maximumSuctionLift: numberString(
        selected.suction_limits.maximum_suction_lift_m,
      ),
      mcfsFlow: numberString(
        selected.operating_envelope.minimum_continuous_stable_flow_m3_s,
      ),
      measuredTraceIds: selected.measured_traces.map((trace) => trace.id),
      minimumFlow: numberString(selected.operating_envelope.minimum_flow_m3_s),
      minimumHead: numberString(selected.operating_envelope.minimum_head_m),
      minimumSuctionPressure: numberString(
        selected.suction_limits.minimum_suction_pressure_absolute_pa,
      ),
      name: selected.curve_set_name,
      revisionLabel: nextLabel,
      testedSpeed: String(selected.tested_speed_rpm),
    };
  });
}
